import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "../cx";
import type { BannerTone } from "../keys";

const TONE: Record<BannerTone, { box: string; label: string; role: "status" | "alert" }> = {
  info: { box: "border-line bg-surface", label: "text-ink-2", role: "status" },
  working: { box: "border-accent-line bg-accent-soft", label: "text-accent", role: "status" },
  warn: { box: "border-warn-line bg-warn-soft", label: "text-warn", role: "status" },
  danger: { box: "border-danger-line bg-danger-soft", label: "text-danger", role: "alert" },
};

export interface BannerProps {
  tone: BannerTone;
  /** Mono uppercase label at the left, e.g. REFINING; defaults to the tone's name. */
  label?: string;
  children: ReactNode;
  /** Right-hand slot: a Button, a link, a progress readout. */
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Full-width strip under the nav or inside a card: mono label, text, action.
 * `working` carries a ProgressBar as its child on the Style Sheet and Progress screens.
 */
export function Banner({ tone, label, children, action, onDismiss, className = "" }: BannerProps) {
  const { t } = useTranslation("components");
  const style = TONE[tone];
  return (
    <div
      role={style.role}
      data-component="Banner"
      data-tone={tone}
      className={cx(
        "flex items-center gap-4 border px-4 py-2.5 text-ui text-ink",
        style.box,
        className,
      )}
    >
      <span className={cx("label-mono shrink-0", style.label)}>
        {label ?? t(`banner.tone.${tone}`)}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t("banner.dismiss")}
          className="shrink-0 font-mono text-label text-ink-2 hover:text-ink"
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
